import { Check, Lock } from '../../../icons';
import { tSettings } from '../../../lib/settings-i18n';
import { PanelHead } from '../PendingRow';
import { useScreenLockStore, LOCK_DESIGNS, type LockDesign } from '../../../store/screenLockStore';

/**
 * 잠금화면 설정 (screen-lock — 2026-08-19) — 보안설정 > 잠금화면 설정.
 *
 * 세 디자인을 **모두 쓸 수 있게** 두고 고르게 한다(2026-08-19 사용자 요구). 고른 것은
 * 기기별로 저장한다 — 잠금은 계정이 아니라 "지금 앉아 있는 자리"의 문제라서다.
 * 미리보기는 실제 가림막을 5초 띄운다 — 작은 그림만으로는 어떤 느낌인지 알기 어렵다.
 */
export function ScreenLockPanel() {
  const settings = useScreenLockStore((s) => s.settings);
  const patch = useScreenLockStore((s) => s.patchSettings);
  const setPreview = useScreenLockStore((s) => s.setPreview);

  return (
    <section className="settings-section" aria-label={tSettings('settings.lock.title')}>
      <PanelHead title={tSettings('settings.lock.title')} desc={tSettings('settings.lock.desc')} />

      <div className="st-rows">
        <div className="st-row">
          <span className="st-row__ico" aria-hidden><Lock size={15} /></span>
          <span className="st-row__main">
            <span className="st-row__title">{tSettings('settings.lock.enable')}</span>
            <span className="st-row__desc">{tSettings('settings.lock.enable_desc')}</span>
          </span>
          <button
            type="button"
            className={`st-switch${settings.enabled ? ' is-on' : ''}`}
            role="switch"
            aria-checked={settings.enabled}
            aria-label={tSettings('settings.lock.enable')}
            onClick={() => patch({ enabled: !settings.enabled })}
          />
        </div>
      </div>

      <div className="st-lock-designs" role="radiogroup" aria-label={tSettings('settings.lock.design_heading')}>
        <p className="st-lock-designs__head">{tSettings('settings.lock.design_heading')}</p>
        <div className="st-lock-designs__grid">
          {LOCK_DESIGNS.map((design) => (
            <DesignCard
              key={design}
              design={design}
              active={settings.design === design}
              disabled={!settings.enabled}
              onPick={() => patch({ design })}
              onPreview={() => setPreview(design)}
            />
          ))}
        </div>
      </div>

      <p className="st-lock-note">{tSettings('settings.lock.preview_note')}</p>
      {!settings.enabled && <p className="st-lock-note">{tSettings('settings.lock.disabled_note')}</p>}
    </section>
  );
}

const LABEL_KEY: Record<LockDesign, string> = {
  blur: 'settings.lock.design.blur',
  cover: 'settings.lock.design.cover',
  sheet: 'settings.lock.design.sheet',
};
const DESC_KEY: Record<LockDesign, string> = {
  blur: 'settings.lock.design.blur_desc',
  cover: 'settings.lock.design.cover_desc',
  sheet: 'settings.lock.design.sheet_desc',
};

function DesignCard({ design, active, disabled, onPick, onPreview }: {
  design: LockDesign;
  active: boolean;
  disabled: boolean;
  onPick: () => void;
  onPreview: () => void;
}) {
  const label = tSettings(LABEL_KEY[design]);
  return (
    <div className={`st-lock-card${active ? ' is-on' : ''}`}>
      <button
        type="button"
        className="st-lock-card__pick"
        role="radio"
        aria-checked={active}
        aria-label={label}
        disabled={disabled}
        onClick={onPick}
      >
        {active && (
          <span className="st-lock-card__chk" aria-hidden><Check size={12} /></span>
        )}
        <DesignThumb design={design} />
        <span className="st-lock-card__meta">
          <b>{label}</b>
          <span>{tSettings(DESC_KEY[design])}</span>
        </span>
      </button>
      <button type="button" className="st-lock-card__pv" onClick={onPreview} disabled={disabled}>
        {tSettings('settings.lock.preview')}
      </button>
    </div>
  );
}

/**
 * 축소 그림 — 실제 화면을 그대로 줄이는 대신 뼈대만 그린다.
 * (실물은 "미리보기" 가 보여준다. 여기서는 셋을 한눈에 구별하는 것이 목적이다.)
 */
function DesignThumb({ design }: { design: LockDesign }) {
  return (
    <span className={`st-lock-thumb st-lock-thumb--${design}`} aria-hidden>
      <span className="st-lock-thumb__app">
        <span className="st-lock-thumb__rail" />
        <span className="st-lock-thumb__side" />
        <span className="st-lock-thumb__main" />
      </span>
      <span className="st-lock-thumb__veil">
        {design === 'cover' && <span className="st-lock-thumb__clock" />}
        <span className="st-lock-thumb__card">
          <span className="st-lock-thumb__dot" />
          <span className="st-lock-thumb__line" />
          <span className="st-lock-thumb__btn" />
        </span>
      </span>
    </span>
  );
}
